import { useEffect, useMemo, useState } from "react";
import { venueName } from "../venues";
import { Card, CardHead, compact, Empty, Freshness, Label, Num, Pill, Skeleton } from "../components/ui";

// The desk at a glance.
//
// Six figures, one chart, then the detail — the summary answers "is the desk
// healthy" without scrolling, and everything below it is evidence for that
// answer. Deliberately the numbers a desk head asks for first: what traded,
// what it earned, what is waiting on a person, and whether the books still
// agree with the venues.

interface Mark {
  asset: string;
  price: number;
  change_24h: number | null;
}

interface OverviewData {
  marks?: Mark[];
  totals: {
    volume_eur_30d: number;
    revenue_eur_30d: number;
    trades_30d: number;
    volume_eur_24h: number;
    revenue_eur_24h: number;
    trades_24h: number;
    active_clients: number;
  };
  series: { date: string; volumeEur: number; revenueEur: number; trades: number }[];
  venues: { venue: string; trades: number; volume_eur: number }[];
  orders_by_status: Record<string, number>;
  attention: {
    pending_approvals: number;
    open_breaks: number;
    last_recon_at: string | null;
    last_recon_matched: number | null;
    last_recon_compared: number | null;
  };
  top_clients: { name: string; volume_eur: number; trades: number }[];
  recent_trades: {
    at: string;
    client: string | null;
    venue: string | null;
    side: string;
    pair: string;
    quantity: string;
    price: string;
    markup_eur: number;
  }[];
}

const auth = () => ({ authorization: `Bearer ${sessionStorage.getItem("otc.session") ?? ""}` });
const POLL_MS = 20_000;

/* ------------------------------------------------------------------- tape */

const MARK_LABEL: Record<string, string> = {
  BTC: "BTC/EUR", ETH: "ETH/EUR", SOL: "SOL/EUR",
  USDT_ERC20: "USDT/EUR", USDC: "USDC/EUR",
};

/** Price precision by magnitude: 66,412.80 and 0.8632 both need to read as
 *  prices, and a fixed decimal count makes one of them wrong. */
function markPrice(n: number): string {
  const dp = n >= 1000 ? 2 : n >= 1 ? 2 : 4;
  return n.toLocaleString("en-US", { minimumFractionDigits: dp, maximumFractionDigits: dp });
}

function Tape({ marks, at }: { marks: Mark[]; at: number | null }) {
  if (marks.length === 0) return null;
  return (
    <div className="tape" aria-label="Reference prices">
      {marks.map((m) => (
        <div className="tape-item" key={m.asset}>
          <span className="a">{MARK_LABEL[m.asset] ?? m.asset}</span>
          <span className="p">{markPrice(m.price)}</span>
          {/* Absent rather than 0.00% when there is no 24h-ago mark to compare
              against — an invented zero would read as "flat", which is a
              different claim from "not known yet". */}
          {m.change_24h !== null && (
            <span className={m.change_24h >= 0 ? "gain" : "loss"}>
              {m.change_24h >= 0 ? "+" : "-"}
              {Math.abs(m.change_24h).toFixed(2)}%
            </span>
          )}
        </div>
      ))}
      <div className="tape-live">
        <Freshness at={at} intervalMs={POLL_MS} />
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ chart */

/**
 * Daily volume. Inline SVG — a charting dependency for one series is weight
 * without benefit, and hand-drawing it means the curve can use the theme
 * tokens directly instead of a library's own palette.
 *
 * The hover readout matters more than it looks: a trend line with no values
 * tells you the shape and nothing else, and the guidance for time series is
 * explicit that focus should reveal the underlying number. The figures are
 * also emitted as a visually-hidden table so the series is reachable without
 * seeing the line at all.
 */
function HeroChart({ series }: { series: OverviewData["series"] }) {
  const [hover, setHover] = useState<number | null>(null);

  const geom = useMemo(() => {
    const w = 1000;
    const h = 210;
    const pad = 6;
    const max = Math.max(...series.map((s) => s.volumeEur), 1);
    const step = series.length > 1 ? (w - pad * 2) / (series.length - 1) : 0;
    const y = (v: number) => h - pad - (v / max) * (h - pad * 3.2);
    return { w, h, pad, max, step, pts: series.map((s, i) => [pad + i * step, y(s.volumeEur)] as const) };
  }, [series]);

  if (series.length < 2) {
    return (
      <div style={{ padding: "var(--s6)" }}>
        <Empty title="Not enough history yet" body="The curve appears once the desk has traded on more than one day." />
      </div>
    );
  }

  const { w, h, pad, max, step, pts } = geom;
  const line = `M ${pts.map(([x, y]) => `${x},${y}`).join(" L ")}`;
  const area = `${line} L ${pts[pts.length - 1]![0]},${h} L ${pts[0]![0]},${h} Z`;
  const active = hover === null ? null : series[hover];

  return (
    <div style={{ marginTop: "var(--s5)", position: "relative" }}>
      <div
        className="t-label"
        style={{ display: "flex", justifyContent: "space-between", padding: "0 var(--s6) 8px" }}
      >
        <span>{series[0]?.date}</span>
        {/* The readout replaces the peak rather than sitting beside it, so the
            line never has two numbers competing for the same glance. */}
        <span className="num" style={{ color: active ? "var(--text)" : undefined }}>
          {active
            ? `${active.date}: ${compact(active.volumeEur)} EUR from ${active.trades} trades`
            : `peak ${compact(max)} EUR`}
        </span>
        <span>{series[series.length - 1]?.date}</span>
      </div>

      <svg
        viewBox={`0 0 ${w} ${h}`}
        style={{ width: "100%", height: 210, display: "block" }}
        preserveAspectRatio="none"
        role="img"
        aria-label={`Daily traded volume over ${series.length} days, peaking at ${compact(max)} euro`}
        onMouseLeave={() => setHover(null)}
        onMouseMove={(e) => {
          const box = e.currentTarget.getBoundingClientRect();
          const rel = ((e.clientX - box.left) / box.width) * w;
          setHover(Math.max(0, Math.min(series.length - 1, Math.round((rel - pad) / step))));
        }}
      >
        <defs>
          <linearGradient id="heroFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="var(--accent)" stopOpacity="0.26" />
            <stop offset="100%" stopColor="var(--accent)" stopOpacity="0" />
          </linearGradient>
          {/* A soft bloom on the stroke only. The curve is the one place the
              page earns a little light; the fill stays flat so the shape
              reads rather than glows. */}
          <filter id="heroGlow" x="-4%" y="-20%" width="108%" height="150%">
            <feGaussianBlur stdDeviation="3" result="b" />
            <feMerge>
              <feMergeNode in="b" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>
        <path d={area} fill="url(#heroFill)" />
        <path
          d={line}
          fill="none"
          stroke="var(--accent)"
          strokeWidth="2"
          strokeLinejoin="round"
          strokeLinecap="round"
          vectorEffect="non-scaling-stroke"
          filter="url(#heroGlow)"
        />
        {active && hover !== null && (
          <g>
            <line
              x1={pts[hover]![0]} y1={0} x2={pts[hover]![0]} y2={h}
              stroke="var(--hairline-strong)" strokeWidth="1"
              strokeDasharray="3 3" vectorEffect="non-scaling-stroke"
            />
            <circle
              cx={pts[hover]![0]} cy={pts[hover]![1]} r="4"
              fill="var(--accent)" stroke="var(--surface)" strokeWidth="2"
              vectorEffect="non-scaling-stroke"
            />
          </g>
        )}
      </svg>
    </div>
  );
}

/* ------------------------------------------------------------------ venues */

/** Share of flow by venue — the visual proof that routing is actually routing. */
function VenueSplit({ venues }: { venues: OverviewData["venues"] }) {
  const total = venues.reduce((s, v) => s + v.volume_eur, 0) || 1;

  // A fixed ramp of the accent hue rather than six unrelated colours. The
  // segments are ordered by size and every one carries its name in the
  // legend, so the chart never depends on telling two blues apart.
  const shade = (i: number) => `color-mix(in srgb, var(--accent) ${Math.max(22, 100 - i * 16)}%, var(--surface-3))`;

  if (venues.length === 0) {
    return (
      <Card pad={false} className="enter">
        <CardHead title="Execution by venue, 30 days" />
        <Empty title="No fills yet" body="Venue share appears after the first execution." />
      </Card>
    );
  }

  return (
    <Card pad={false} className="enter">
      <CardHead title="Execution by venue, 30 days" />
      <div style={{ padding: "16px 20px 20px" }}>
        <div style={{ display: "flex", height: 8, borderRadius: 999, overflow: "hidden", marginBottom: 18, gap: 2 }}>
          {venues.map((v, i) => (
            <div
              key={v.venue}
              style={{ width: `${(v.volume_eur / total) * 100}%`, background: shade(i) }}
              title={`${venueName(v.venue)}: ${((v.volume_eur / total) * 100).toFixed(1)}%`}
            />
          ))}
        </div>
        {venues.map((v, i) => (
          <div key={v.venue} style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
            <span style={{ width: 8, height: 8, borderRadius: 2, background: shade(i), flexShrink: 0 }} />
            <span style={{ flex: 1, fontWeight: 500 }}>{venueName(v.venue)}</span>
            <span className="dim t-sm num">{v.trades} fills</span>
            <span className="num" style={{ width: 80, textAlign: "right" }}>{compact(v.volume_eur)}</span>
            <span className="dim num t-sm" style={{ width: 44, textAlign: "right" }}>
              {((v.volume_eur / total) * 100).toFixed(0)}%
            </span>
          </div>
        ))}
      </div>
    </Card>
  );
}

/* -------------------------------------------------------------------- page */

export function Overview() {
  const [data, setData] = useState<OverviewData | null>(null);
  const [at, setAt] = useState<number | null>(null);

  useEffect(() => {
    let alive = true;
    const load = () =>
      fetch("/api/overview", { headers: auth() })
        .then((r) => (r.ok ? r.json() : null))
        .then((d) => {
          if (!alive || !d) return;
          setData(d);
          setAt(Date.now());
        })
        .catch(() => undefined);
    void load();
    const t = setInterval(load, POLL_MS);
    return () => {
      alive = false;
      clearInterval(t);
    };
  }, []);

  if (!data) {
    // The skeleton mirrors the sheet, not a stat grid — a placeholder in the
    // wrong shape makes the real layout snap into place rather than resolve.
    return (
      <>
        <div className="sheet" style={{ height: 330 }} />
        <div style={{ height: 18 }} />
        <Skeleton rows={5} />
      </>
    );
  }

  const { totals, attention } = data;
  const waiting = attention.pending_approvals + attention.open_breaks;
  const recon =
    attention.last_recon_compared !== null
      ? `${attention.last_recon_matched}/${attention.last_recon_compared} positions agree`
      : "no run yet";

  return (
    <>
      {data.marks && data.marks.length > 0 && (
        <div className="tape-bleed">
          <Tape marks={data.marks} at={at} />
        </div>
      )}

      {/* The hero sheet: the headline figure and the curve behind it are one
          surface, so the eye lands in a single place. The four secondary
          figures sit inline to its right, divided by rules — as separate
          cards they would restore the equal-weight row this replaces. */}
      <div className="sheet enter">
        <div className="sheet-head">
          <div>
            <div className="stat-label">30-day volume</div>
            <div className="sheet-figure">
              {compact(totals.volume_eur_30d)}
              <span className="stat-unit">EUR</span>
            </div>
            <div className="t-sm dim" style={{ marginTop: 11, display: "flex", alignItems: "center", gap: 9, flexWrap: "wrap" }}>
              <span>
                <Num value={totals.trades_30d} /> trades from <Num value={totals.active_clients} /> counterparties
              </span>
              <Pill tone="good">+<Num value={totals.revenue_eur_30d} dp={0} /> EUR earned</Pill>
            </div>
          </div>

          <div className="sheet-mini">
            <div>
              <div className="stat-label">Volume 24h</div>
              <div className="v">{compact(totals.volume_eur_24h)}<span className="stat-unit">EUR</span></div>
              <div className="s"><Num value={totals.trades_24h} /> trades</div>
            </div>
            <div>
              <div className="stat-label">Revenue 24h</div>
              <div className="v gain"><Num value={totals.revenue_eur_24h} dp={0} /><span className="stat-unit">EUR</span></div>
              <div className="s">markup</div>
            </div>
            <div>
              <div className="stat-label">Clients</div>
              <div className="v"><Num value={totals.active_clients} /></div>
              <div className="s">active</div>
            </div>
            <div>
              <div className="stat-label">Attention</div>
              <div className={`v${waiting > 0 ? " warn" : " dimmer"}`}><Num value={waiting} /></div>
              <div className="s">
                {waiting > 0 ? `${attention.pending_approvals} approvals, ${attention.open_breaks} breaks` : "nothing waiting"}
              </div>
            </div>
          </div>
        </div>

        <HeroChart series={data.series} />
      </div>

      <div className="section grid grid-2">
        <VenueSplit venues={data.venues} />

        <Card pad={false} className="enter">
          <CardHead
            title="Reconciliation"
            right={
              attention.open_breaks === 0
                ? <Pill tone="good" dot>Clean</Pill>
                : <Pill tone="bad" dot>{attention.open_breaks} open</Pill>
            }
          />
          <div style={{ padding: "16px 20px 20px" }}>
            <div className="t-body dim">{recon}</div>
            <div className="t-sm dimmer" style={{ marginTop: 4 }}>
              {attention.last_recon_at
                ? `Last run ${new Date(attention.last_recon_at).toLocaleString()}`
                : "The first run lands within 30 minutes of deploy."}
            </div>

            <div style={{ borderTop: "1px solid var(--hairline)", margin: "18px 0 14px" }} />

            <Label>Top counterparties, 30 days</Label>
            <div style={{ marginTop: 12 }}>
              {data.top_clients.length === 0 && <span className="dim t-sm">No client flow yet.</span>}
              {data.top_clients.map((c) => (
                <div key={c.name} style={{ display: "flex", gap: 10, marginBottom: 9 }}>
                  <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {c.name}
                  </span>
                  <span className="dim num t-sm">{c.trades}</span>
                  <span className="num" style={{ width: 86, textAlign: "right" }}>{compact(c.volume_eur)} EUR</span>
                </div>
              ))}
            </div>
          </div>
        </Card>
      </div>

      <div className="section">
        <Card pad={false} className="enter">
          <CardHead title="Latest fills" />
          {data.recent_trades.length === 0 ? (
            <Empty title="No fills yet" body="Executions appear here the moment the first order is routed and filled." />
          ) : (
            <div className="table-wrap flush">
              <table>
                <thead>
                  <tr>
                    <th>Time</th><th>Client</th><th>Side</th><th>Pair</th>
                    <th className="right">Quantity</th><th className="right">Price</th>
                    <th>Venue</th><th className="right">Markup</th>
                  </tr>
                </thead>
                <tbody>
                  {data.recent_trades.map((t) => (
                    // Keyed on the fill's own identity, not the array index —
                    // this list is re-fetched every 20s and an index key makes
                    // React reuse the wrong row when a new fill lands on top.
                    <tr key={`${t.at}-${t.pair}-${t.quantity}`}>
                      <td className="dim num" style={{ whiteSpace: "nowrap" }}>
                        {new Date(t.at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
                      </td>
                      <td style={{ maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {t.client ?? <span className="dimmer">—</span>}
                      </td>
                      <td>
                        <Pill tone={t.side === "buy" ? "good" : "info"}>{t.side.toUpperCase()}</Pill>
                      </td>
                      <td style={{ fontWeight: 500 }}>{t.pair}</td>
                      <td className="right num">{Number(t.quantity).toLocaleString("en-US", { maximumFractionDigits: 8 })}</td>
                      <td className="right num">{Number(t.price).toLocaleString("en-US", { maximumFractionDigits: 2 })}</td>
                      <td className="dim">{venueName(t.venue)}</td>
                      <td className="right num gain">+<Num value={t.markup_eur} dp={2} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>
    </>
  );
}
